/** SCR-17 / 18 — Sales Manager: partner lead review queue, decisions and sales-rep assignment. */
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, Badge, Modal, Field, Tabs, SearchInput, Drawer, Spinner, ErrorState } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import { pushToast } from '../ui/uiSlice.js';
import { money, date, dateTime } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { SubmissionBadge, errorText } from './partnerShared.jsx';
import { Conversation, Documents } from './LeadCollab.jsx';

const TABS = [
  { key: 'in_review', label: 'In review' }, { key: 'information_requested', label: 'Information requested' },
  { key: 'approved', label: 'Approved' }, { key: 'rejected', label: 'Rejected' }, { key: 'in_review,information_requested,approved,rejected', label: 'All' },
];

export default function PartnerReviewPage() {
  const [status, setStatus] = useState('in_review');
  const [dupOnly, setDupOnly] = useState(false);
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 300);
  const [openId, setOpenId] = useState(null);
  const list = useInfiniteQuery({
    queryKey: ['partner-review', { status, dupOnly, dq }],
    queryFn: ({ pageParam }) => api.reviewQueue({ status, q: dq, has_duplicates: dupOnly ? 'true' : '', cursor: pageParam, limit: 25 }),
    initialPageParam: undefined, getNextPageParam: (l) => l.page?.next_cursor ?? undefined,
  });
  const rows = (list.data?.pages ?? []).flatMap((p) => p.data);
  const columns = [
    { key: 'partner_lead_id', header: 'Lead ID', render: (r) => <span className="fw-600">{r.partner_lead_id}</span> },
    { key: 'lead_name', header: 'Lead', render: (r) => (<div><div className="fw-600">{r.lead_name}</div><div className="t-xs text-muted">{r.contact_person} · {r.email}</div></div>) },
    { key: 'partner', header: 'Partner', render: (r) => (<div>{r.partner.name}<div className="t-xs text-muted">{r.partner.kind === 'company' ? 'Company' : 'Individual'} · by {r.submitted_by.name}</div></div>) },
    { key: 'product_interest', header: 'Interest' },
    { key: 'estimated_value', header: 'Value', align: 'end', render: (r) => (r.estimated_value != null ? money(r.estimated_value, r.currency) : '—') },
    { key: 'dup', header: 'Duplicate', render: (r) => (r.has_duplicate_flag ? <Badge tone="amber"><i className="bi bi-exclamation-triangle" aria-hidden="true" /> Possible</Badge> : '—') },
    { key: 'submission_status', header: 'Status', render: (r) => <SubmissionBadge status={r.submission_status} /> },
    { key: 'submitted_at', header: 'Submitted', render: (r) => date(r.submitted_at) },
  ];
  return (
    <>
      <PageHeader title="Partner lead review" subtitle="Approve, reject or ask for more information on partner submissions" />
      <Tabs value={status} onChange={setStatus} tabs={TABS} />
      <Card className="mt-3" bare>
        <div className="d-flex flex-wrap gap-2 p-3 pb-2 align-items-center">
          <SearchInput value={q} onChange={setQ} placeholder="Search lead, partner, ID…" />
          <label className="t-sm d-flex align-items-center gap-1"><input type="checkbox" checked={dupOnly} onChange={(e) => setDupOnly(e.target.checked)} /> Duplicate warnings only</label>
        </div>
        <DataTable columns={columns} rows={rows} isLoading={list.isLoading} error={list.error} onRetry={list.refetch} onRowClick={(r) => setOpenId(r.id)} emptyTitle="Nothing to review" />
        {list.hasNextPage && <div className="p-3 text-center"><button type="button" className="btn btn-edit btn-sm-x" onClick={() => list.fetchNextPage()}>Load more</button></div>}
      </Card>
      <ReviewDrawer id={openId} onClose={() => setOpenId(null)} />
    </>
  );
}

function ReviewDrawer({ id, onClose }) {
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const [tab, setTab] = useState('details');
  const [action, setAction] = useState(null); // approve | reject | info | rep
  const q = useQuery({ queryKey: ['partner-review', 'detail', id], queryFn: () => api.reviewDetail(id), enabled: Boolean(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['partner-review'] }); qc.invalidateQueries({ queryKey: ['partner'] }); };
  const l = q.data;
  const done = (msg) => { setAction(null); refresh(); dispatch(pushToast({ tone: 'success', message: msg })); };
  return (
    <Drawer open={Boolean(id)} onClose={() => { setAction(null); setTab('details'); onClose(); }} width={720} title={l?.lead_name} subtitle={l ? `${l.partner_lead_id} · ${l.partner.name}` : undefined}
      footer={l && (
        <div className="d-flex flex-wrap gap-2">
          {l.submission_status === 'in_review' && (<>
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => setAction('approve')}><i className="bi bi-check-lg me-1" aria-hidden="true" />Approve</button>
            <button type="button" className="btn btn-edit btn-sm-x" onClick={() => setAction('info')}>Request information</button>
          </>)}
          {['in_review', 'information_requested'].includes(l.submission_status) && <button type="button" className="btn btn-delete btn-sm-x" onClick={() => setAction('reject')}>Reject</button>}
          {l.submission_status === 'approved' && <button type="button" className="btn btn-save btn-sm-x" onClick={() => setAction('rep')}>{l.sales_rep ? 'Change sales rep' : 'Assign sales rep'}</button>}
        </div>
      )}>
      {q.isLoading && <Spinner />}
      {q.isError && <ErrorState error={q.error} onRetry={q.refetch} />}
      {l && (
        <>
          <div className="d-flex gap-2 align-items-center mb-3"><SubmissionBadge status={l.submission_status} />{l.crm_lead && <Badge tone="emerald">CRM {l.crm_lead.lead_no} · {l.crm_lead.status}</Badge>}</div>
          {l.has_duplicate_flag && (
            <div className="alert alert-warning t-sm">
              <b>Possible duplicate.</b> {l.duplicate_flags.map((d, i) => (<div key={i}>• {d.source === 'crm_lead' ? 'CRM lead' : 'Partner submission'} {d.number} matches on {d.field}{d.other_partner ? ' — belongs to another partner' : ''}</div>))}
            </div>
          )}
          {l.submission_status === 'rejected' && <div className="alert alert-danger t-sm"><b>Rejected:</b> {l.rejection_reason}</div>}
          <Tabs value={tab} onChange={setTab} tabs={[{ key: 'details', label: 'Details' }, { key: 'conversation', label: 'Conversation' }, { key: 'documents', label: 'Documents', badge: l.documents.length }]} />
          <div className="pt-3">
            {tab === 'details' && (
              <div className="row g-3 t-sm">
                {[['Partner', `${l.partner.name} (${l.partner.code})`], ['Submitted by', l.submitted_by.name], ['Contact', l.contact_person], ['Designation', l.designation], ['Email', l.email], ['Mobile', l.mobile], ['Website', l.website], ['Industry', l.industry], ['Country', l.country], ['State / City', l.state_city], ['Interest', l.product_interest], ['Estimated value', l.estimated_value != null ? money(l.estimated_value, l.currency) : null], ['Timeline', l.purchase_timeline], ['Submitted', dateTime(l.submitted_at)], ['Sales rep', l.sales_rep?.name], ['Reviewed remarks', l.review_remarks]].map(([k, v]) => (
                  <div className="col-md-6" key={k}><div className="field-x-label mb-1">{k}</div>{v || '—'}</div>
                ))}
                <div className="col-12"><div className="field-x-label mb-1">Description</div><div style={{ whiteSpace: 'pre-wrap' }}>{l.description}</div></div>
              </div>
            )}
            {tab === 'conversation' && <Conversation leadId={id} internal locked={l.submission_status === 'rejected'} />}
            {tab === 'documents' && <Documents leadId={id} internal locked={l.submission_status === 'rejected'} />}
          </div>
        </>
      )}
      {l && <ActionModal action={action} lead={l} onClose={() => setAction(null)} onDone={done} />}
    </Drawer>
  );
}

function ActionModal({ action, lead, onClose, onDone }) {
  const [text, setText] = useState('');
  const [rep, setRep] = useState('');
  const [err, setErr] = useState('');
  const [seen, setSeen] = useState(null);
  if (action && seen !== action) { setSeen(action); setText(''); setRep(''); setErr(''); }
  if (!action && seen) setSeen(null);
  const users = useQuery({ queryKey: ['partner-review', 'sales-reps'], queryFn: api.reviewSalesReps, enabled: action === 'approve' || action === 'rep' });
  const reps = users.data?.data ?? [];
  const run = useMutation({
    mutationFn: () => ({
      approve: () => api.approveLead(lead.id, { remarks: text.trim() || undefined, sales_rep_id: rep || undefined }),
      reject: () => api.rejectLead(lead.id, { reason: text.trim() }),
      info: () => api.requestInformation(lead.id, { message: text.trim() }),
      rep: () => api.assignSalesRep(lead.id, { sales_rep_id: rep, remarks: text.trim() || undefined }),
    })[action](),
    onSuccess: () => onDone({ approve: 'Lead approved and added to the CRM', reject: 'Lead rejected', info: 'Information requested', rep: 'Sales representative assigned' }[action]),
    onError: (e) => setErr(errorText(e)),
  });
  if (!action) return null;
  const cfg = {
    approve: { title: 'Approve lead', label: 'Remarks (optional)', cta: 'Approve', need: false, rep: true },
    reject: { title: 'Reject lead', label: 'Reason for rejection', cta: 'Reject', need: true },
    info: { title: 'Request more information', label: 'What do you need from the partner?', cta: 'Send request', need: true },
    rep: { title: 'Assign sales representative', label: 'Remarks (optional)', cta: 'Assign', need: false, rep: true, repRequired: true },
  }[action];
  const REJECT_MIN = 20;
  const tooShort = action === 'reject' && text.trim().length < REJECT_MIN;
  const invalid = (cfg.need && !text.trim()) || tooShort || (cfg.repRequired && !rep);
  return (
    <Modal open onClose={onClose} title={cfg.title}
      footer={(<><button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
        <button type="button" className={`btn btn-sm-x ${action === 'reject' ? 'btn-delete' : 'btn-save'}`} disabled={invalid || run.isPending} onClick={() => run.mutate()}>{run.isPending ? <ButtonSpinner /> : cfg.cta}</button></>)}>
      {err && <div className="alert alert-danger py-2 t-sm">{err}</div>}
      {action === 'approve' && <div className="t-sm text-muted mb-3">Approving creates the CRM lead (once) and notifies the partner. You can assign a sales representative now or later.</div>}
      {cfg.rep && (
        <div className="mb-3"><Field label={`Sales representative${cfg.repRequired ? '' : ' (optional)'}`} required={cfg.repRequired}>
          <select className="form-select" value={rep} onChange={(e) => setRep(e.target.value)}><option value="">{users.isLoading ? 'Loading…' : 'Choose…'}</option>{reps.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}</select>
        </Field></div>
      )}
      {action === 'reject' && <div className="t-sm text-muted mb-2">The partner sees this reason, so say clearly why the lead is being rejected and what, if anything, they could fix.</div>}
      <Field label={cfg.label} required={cfg.need}>
        <textarea className="form-control" rows={4} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={action === 'reject' ? 'e.g. The customer is already in our pipeline under another partner, so we cannot accept this referral.' : undefined} />
      </Field>
      {action === 'reject' && <div className={`t-2xs mt-1 ${tooShort ? 'text-danger' : 'text-muted'}`}>{text.trim().length}/{REJECT_MIN} characters minimum</div>}
    </Modal>
  );
}
